/**
 * useMondayComParser - Parse Monday.com Excel format
 *
 * Expected format:
 * A1: Board Title
 * A2: Board Description
 * A4: First Group Name
 * A5: Column Headers
 * A6+: Data rows for that group
 * (Empty row)
 * Next row: Next Group Name
 * (Repeat pattern for each group)
 */
import { useState, useCallback } from 'react';
/**
 * Parse XLSX file in Monday.com format
 */
async function parseXLSXMondayFormat(file) {
    try {
        // Dynamically import xlsx
        const XLSX = await import('xlsx');
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = e.target?.result;
                    const workbook = XLSX.read(data, { type: 'binary' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    // Get raw data with all values
                    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
                    const result = parseMondayFormatFromWorksheet(worksheet, range, XLSX);
                    resolve(result);
                }
                catch (error) {
                    reject(error);
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsBinaryString(file);
        });
    }
    catch (error) {
        throw new Error('Failed to parse Excel file. Make sure xlsx library is installed.');
    }
}
/**
 * Parse worksheet data in Monday.com format
 */
function parseMondayFormatFromWorksheet(worksheet, range, XLSX) {
    const rows = [];
    // Extract all cell values row by row
    for (let row = range.s.r; row <= range.e.r; row++) {
        const rowData = [];
        for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_col(col) + (row + 1);
            const cell = worksheet[cellAddress];
            const value = cell?.v ?? null;
            rowData.push(value);
        }
        rows.push(rowData);
    }
    // Extract board metadata
    const boardTitle = rows[0]?.[0]?.toString().trim() || 'Imported Board';
    const boardDescription = rows[1]?.[0]?.toString().trim() || '';
    // Parse groups and data
    const groups = [];
    const allColumns = new Set();
    let currentRowIndex = 3; // Start from row 4 (0-indexed as 3)
    while (currentRowIndex < rows.length) {
        const row = rows[currentRowIndex];
        // Check if this is an empty row
        if (!row || row.every((cell) => cell === null || cell === '')) {
            currentRowIndex++;
            continue;
        }
        // Check if this might be a group header (has content in first cell, usually text)
        const firstCellValue = row[0];
        if (firstCellValue && typeof firstCellValue === 'string') {
            const groupName = firstCellValue.trim();
            // Next row should contain column headers
            const columnHeadersRow = rows[currentRowIndex + 1];
            if (!columnHeadersRow) {
                currentRowIndex++;
                continue;
            }
            // Extract column headers (skip empty ones)
            const columnHeaders = columnHeadersRow
                .map((cell, idx) => {
                const header = cell?.toString().trim();
                return header ? { header, index: idx } : null;
            })
                .filter((item) => item !== null);
            if (columnHeaders.length === 0) {
                currentRowIndex += 2;
                continue;
            }
            // Extract column names for this group
            const groupColumnNames = columnHeaders.map((c) => c.header);
            groupColumnNames.forEach((col) => allColumns.add(col));
            // Parse tasks for this group (start from row after column headers)
            const tasks = [];
            let taskRowIndex = currentRowIndex + 2;
            while (taskRowIndex < rows.length) {
                const taskRow = rows[taskRowIndex];
                // Stop if we hit an empty row (end of group)
                if (!taskRow || taskRow.every((cell) => cell === null || cell === '')) {
                    break;
                }
                // Stop if first cell looks like a new group (has text but different pattern)
                if (taskRow[0] &&
                    typeof taskRow[0] === 'string' &&
                    columnHeaders.every((c) => taskRow[c.index] === null || taskRow[c.index] === '')) {
                    break;
                }
                // Create task object using column headers
                const task = {};
                columnHeaders.forEach(({ header, index }) => {
                    const value = taskRow[index];
                    task[header] = value !== null && value !== '' ? value : null;
                });
                // Only add if task has at least some data
                if (Object.values(task).some((v) => v !== null)) {
                    tasks.push(task);
                }
                taskRowIndex++;
            }
            // Add group if it has tasks
            if (tasks.length > 0) {
                groups.push({
                    name: groupName,
                    tasks,
                    columnHeaders: groupColumnNames,
                });
            }
            currentRowIndex = taskRowIndex;
        }
        else {
            currentRowIndex++;
        }
    }
    return {
        boardTitle,
        boardDescription,
        groups,
        allColumns: Array.from(allColumns),
    };
}
export function useMondayComParser() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const parse = useCallback(async (file) => {
        setIsLoading(true);
        setError(null);
        try {
            // Validate file
            if (!file) {
                throw new Error('No file selected');
            }
            const fileName = file.name.toLowerCase();
            const isXLSX = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
            if (!isXLSX) {
                throw new Error('Only Excel files (.xlsx, .xls) are supported for Monday.com format');
            }
            // Parse file
            const result = await parseXLSXMondayFormat(file);
            // Validate result
            if (!result.boardTitle) {
                throw new Error('Could not extract board title from A1');
            }
            if (result.groups.length === 0) {
                throw new Error('No groups found. Make sure A4 contains group name, A5 contains column headers, and A6+ contains data rows');
            }
            setIsLoading(false);
            return result;
        }
        catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Failed to parse file';
            setError(errorMsg);
            setIsLoading(false);
            return null;
        }
    }, []);
    return { parse, isLoading, error };
}
